/**
 * `renderPolstCard` — the core polst rendering primitive for the POLST
 * embedding widget. Writes title, brand attribution, option cards, tally
 * bar, and a footer (revote link + share) into the Shadow DOM that
 * `createRoot()` prepared. No iframe, no fetch-on-render — the card is
 * declarative: hand it a `PolstDTO` + an injected voter, get back an
 * imperative handle with `update`, `destroy`, `getState`.
 *
 * The vote flow is an optimistic state machine:
 *
 *   idle ──▶ pending ──(ok)──▶ voted ──("change my vote")──▶ idle
 *     │                 │
 *     │                 └──(err)──▶ error (rollback optimistic tally) ──▶ idle
 *
 * Implementation notes:
 *   - The card does not reach into the network itself. Consumers pass a
 *     `vote(polstId, option)` callback (defaulting to the module-scope
 *     `vote` from `./api`). This keeps the card framework-free and lets
 *     the campaign player (POL-584) reuse it with a different voter.
 *   - Zero runtime deps: everything here is hand-rolled DOM.
 *   - jsdom-friendly: the file never references globals other than what
 *     the `Document` it is handed provides, so tests can feed a stub
 *     document for deterministic rendering.
 */
import { vote as defaultVote } from "./api";
import { createLiveRegion, type LiveRegionHandle } from "./a11y";
import { getAppOrigin } from "./config";

/**
 * Minimal polst DTO shape consumed by the card. Kept in sync with the
 * canonical backend PolstDTO (`apps/backend/src/rest/mappers/types.ts`)
 * but deliberately hand-written so the widget package carries no
 * import of the backend source tree.
 */
export interface PolstCardDTO {
  shortId: string;
  title: string;
  status: "scheduled" | "open" | "closed";
  startsAt: string | null;
  endsAt: string | null;
  optionA: { label: string | null; imageUrl: string | null };
  optionB: { label: string | null; imageUrl: string | null };
  tallies: { optionA: number; optionB: number; total: number };
  brand: { slug: string; name: string } | null;
  createdAt?: string;
}

export type PolstCardStatus =
  | "idle"
  | "pending"
  | "voted"
  | "error"
  | "closed"
  | "scheduled";

export interface PolstCardState {
  status: PolstCardStatus;
  choice: "A" | "B" | null;
}

export interface PolstCardOptions {
  /**
   * Injected voter. Defaults to the module-scope `vote(id, option)` from
   * `./api`. The return value is `unknown` because campaign consumers
   * return their own richer shapes; the card only cares whether the
   * promise resolves.
   */
  vote?: (polstId: string, option: "A" | "B") => Promise<unknown>;
  /** Fired after an optimistic vote commits successfully. */
  onVote?: (event: { polstId: string; option: "A" | "B" }) => void;
  /** Fired when a server vote rejects and the card rolls back. */
  onVoteError?: (err: unknown) => void;
  /** Hide the share button in the footer (default: visible). */
  hideShare?: boolean;
  /** Hide the "by <brand>" attribution line. */
  hideBrand?: boolean;
  /**
   * POL-644 — When `true`, suppresses the "Powered by Polst" watermark
   * footer. Wire from `brandTheme.hideWatermark` — the backend already
   * gates that flag on the brand's `embed.hideWatermark` entitlement
   * (POL-650), so this option is a pure data flow with no client-side
   * premium check.
   */
  hideWatermark?: boolean;
  /**
   * `document` override. ShadowRoots don't own a Document — `ownerDocument`
   * returns the host document, which is what we want by default. Tests
   * can pin this if they ever need to render against a detached doc.
   */
  document?: Document;
  /** Clock override for deterministic "voting starts in" copy. */
  now?: () => Date;
}

export interface PolstHandle {
  /** Re-render with a new DTO. Preserves vote state if the polst id matches. */
  update(polst: PolstCardDTO): void;
  /** Detach the card; further handle calls become no-ops. */
  destroy(): void;
  /** Snapshot of the current state machine. */
  getState(): PolstCardState;
}

/**
 * Scoped stylesheet. Every rule is prefixed with `.polst-card` so a
 * future renderer rendering alongside it in the same shadow tree can
 * coexist without CSS bleed. Token-only values; no hard-coded palette.
 */
const CARD_CSS = `
.polst-card {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-md);
  padding: var(--polst-spacing-lg);
  background: var(--polst-bg);
  color: var(--polst-fg);
  border-radius: var(--polst-radius);
  box-sizing: border-box;
  width: 100%;
}
.polst-card[data-size="sm"] { padding: var(--polst-spacing-md); gap: var(--polst-spacing-sm); }
.polst-card[data-size="lg"] { padding: var(--polst-spacing-xl); gap: var(--polst-spacing-lg); }
.polst-card[data-size="full"] { padding: 0; gap: 0; border-radius: 0; }
.polst-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
}
.polst-card[data-size="lg"] .polst-card__title { font-size: 22px; }
.polst-card[data-size="sm"] .polst-card__title { font-size: 16px; }
.polst-card__brand {
  margin: 0;
  font-size: 13px;
  color: var(--polst-muted-fg);
}
.polst-card__brand a { color: inherit; text-decoration: underline; }
.polst-card__status {
  display: inline-block;
  font-size: 12px;
  padding: 2px var(--polst-spacing-sm);
  border-radius: 999px;
  background: var(--polst-accent);
  color: var(--polst-accent-fg);
  align-self: flex-start;
}
.polst-card__options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--polst-spacing-md);
}
.polst-card[data-size="sm"] .polst-card__options { gap: var(--polst-spacing-sm); }
.polst-card__option {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-sm);
  border-radius: var(--polst-radius);
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--polst-fg) 12%, transparent);
  background: color-mix(in srgb, var(--polst-bg) 92%, var(--polst-fg) 8%);
  padding-bottom: var(--polst-spacing-sm);
}
.polst-card__option-image {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  background: color-mix(in srgb, var(--polst-fg) 8%, transparent);
}
.polst-card__option-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: color-mix(in srgb, var(--polst-fg) 8%, transparent);
  color: var(--polst-fg);
  font-size: 32px;
  font-weight: 700;
  opacity: 0.6;
}
.polst-card__option-figure {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-sm);
}
.polst-card__option-label {
  margin: 0 var(--polst-spacing-sm);
  font-size: 14px;
  font-weight: 500;
}
.polst-card__vote {
  margin: 0 var(--polst-spacing-sm);
  padding: var(--polst-spacing-sm) var(--polst-spacing-md);
  background: var(--polst-accent);
  color: var(--polst-accent-fg);
  border: 0;
  border-radius: var(--polst-radius);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.polst-card__vote:hover:not([disabled]) { background: var(--polst-button-hover); }
.polst-card__vote:focus-visible {
  outline: 2px solid var(--polst-accent);
  outline-offset: 2px;
}
.polst-card__vote[disabled] { opacity: 0.5; cursor: not-allowed; }
.polst-card__vote[aria-checked="true"] {
  background: var(--polst-button-hover);
}
.polst-card__tally {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-xs);
}
.polst-card__tally-bar {
  position: relative;
  display: flex;
  width: 100%;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: color-mix(in srgb, var(--polst-fg) 12%, transparent);
}
.polst-card__tally-bar-fill {
  background: var(--polst-accent);
  height: 100%;
  transition: width 180ms ease-out;
}
.polst-card__tally-bar-fill--b {
  background: color-mix(in srgb, var(--polst-accent) 55%, var(--polst-fg) 10%);
}
.polst-card__tally-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--polst-muted-fg);
}
.polst-card__total {
  font-size: 12px;
  color: var(--polst-muted-fg);
}
.polst-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--polst-spacing-sm);
  font-size: 13px;
}
.polst-card__revote,
.polst-card__share {
  background: none;
  border: 0;
  padding: 0;
  color: var(--polst-accent);
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
}
.polst-card__voted-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: var(--polst-spacing-sm) var(--polst-spacing-md);
  border-radius: var(--polst-radius);
  background: color-mix(in srgb, var(--polst-accent) 18%, transparent);
}
.polst-card__error {
  font-size: 13px;
  color: var(--polst-error-fg);
}
.polst-card__scheduled-badge {
  font-size: 12px;
  color: var(--polst-muted-fg);
}
.polst-card__watermark {
  font-size: 11px;
  color: var(--polst-muted-fg);
  text-align: right;
  letter-spacing: 0.02em;
  opacity: 0.85;
}
.polst-card__watermark a {
  color: inherit;
  text-decoration: underline;
}
@media (prefers-reduced-motion: reduce) {
  .polst-card__tally-bar-fill { transition: none !important; }
  .polst-card * { animation: none !important; transition: none !important; }
}
`;

const STYLE_MARKER = "data-polst-card-styles";

function ensureStyles(root: ShadowRoot, doc: Document): void {
  // Render stylesheet once per shadow root. We use a marker attribute so
  // repeat `renderPolstCard` invocations don't stack duplicate <style>
  // blocks if the consumer re-mounts into the same root.
  if (root.querySelector(`style[${STYLE_MARKER}]`)) return;
  const styleEl = doc.createElement("style");
  styleEl.setAttribute(STYLE_MARKER, "");
  styleEl.textContent = CARD_CSS;
  root.appendChild(styleEl);
}

function findWidgetContainer(root: ShadowRoot): Element {
  // `createRoot()` installs a single `[data-polst-widget-root]` wrapper.
  // Prefer it so we don't accidentally write underneath the stylesheet
  // element or stomp on anything the consumer attached manually.
  const container = root.querySelector("[data-polst-widget-root]");
  if (container) return container;
  return root as unknown as Element;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

function percent(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Math.max(0, Math.min(100, Math.round((numerator / denominator) * 100)));
}

function srcsetFor(url: string): string {
  // Browsers honour the `srcset` candidate list as a preference, not a
  // mandate — if the 2x URL 404s the browser retries the 1x src. For the
  // embed surface we have no CDN-side 2x file yet, so we just reference
  // the same URL twice; this still sets `currentSrc` correctly on
  // high-DPI displays.
  return `${url} 1x, ${url} 2x`;
}

function scheduledCopy(polst: PolstCardDTO, now: () => Date): string {
  if (!polst.startsAt) return "Voting opens soon";
  const start = new Date(polst.startsAt).getTime();
  const diff = start - now().getTime();
  if (!Number.isFinite(diff) || diff <= 0) return "Voting opens soon";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `Voting opens in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Voting opens in ${hours}h`;
  const days = Math.round(hours / 24);
  return `Voting opens in ${days}d`;
}

export function renderPolstCard(
  root: ShadowRoot,
  polst: PolstCardDTO,
  options: PolstCardOptions = {},
): PolstHandle {
  if (!root || typeof (root as unknown as { appendChild?: unknown }).appendChild !== "function") {
    throw new Error("renderPolstCard(): root must be a ShadowRoot");
  }
  if (!polst || typeof polst.shortId !== "string") {
    throw new Error("renderPolstCard(): polst.shortId is required");
  }

  // ShadowRoot implements Node but doesn't own a Document; `ownerDocument`
  // resolves to the host document, which is what element creation needs.
  const doc =
    options.document ??
    ((root as unknown as { ownerDocument?: Document }).ownerDocument as Document);
  if (!doc) {
    throw new Error("renderPolstCard(): unable to resolve Document from root");
  }

  const voter = options.vote ?? defaultVote;
  const now = options.now ?? (() => new Date());

  ensureStyles(root, doc);
  const widgetContainer = findWidgetContainer(root);

  // Mutable model. We hold the DTO + overlay counters so optimistic
  // updates don't stomp the server-provided tallies until we either
  // settle (voted) or rollback (error).
  let current: PolstCardDTO = clone(polst);
  let tallyA = polst.tallies.optionA;
  let tallyB = polst.tallies.optionB;
  let total = polst.tallies.total;
  let state: PolstCardState = resolveInitialState(polst);
  let destroyed = false;
  let lastError: unknown = null;

  const cardEl = doc.createElement("section");
  cardEl.className = "polst-card";
  cardEl.setAttribute("data-polst-id", polst.shortId);
  cardEl.setAttribute("role", "group");
  // Title id is used by `aria-labelledby` on the <section> and on the
  // options radiogroup — keep it stable per card.
  const titleId = `polst-card-${polst.shortId}-title`;
  cardEl.setAttribute("aria-labelledby", titleId);

  // Live region for tally announcements. Created once per card; screen
  // readers will hear the updated total after every successful vote.
  const liveRegion: LiveRegionHandle = createLiveRegion(doc);
  cardEl.appendChild(liveRegion.el);

  widgetContainer.appendChild(cardEl);

  function clone(p: PolstCardDTO): PolstCardDTO {
    // Shallow structural clone — card never mutates arrays or nested
    // references after hydration, so a spread per leaf is sufficient.
    return {
      ...p,
      optionA: { ...p.optionA },
      optionB: { ...p.optionB },
      tallies: { ...p.tallies },
      brand: p.brand ? { ...p.brand } : null,
    };
  }

  function resolveInitialState(p: PolstCardDTO): PolstCardState {
    if (p.status === "closed") return { status: "closed", choice: null };
    if (p.status === "scheduled") return { status: "scheduled", choice: null };
    return { status: "idle", choice: null };
  }

  function render(): void {
    if (destroyed) return;
    // Preserve the live region across re-renders so SR announcements
    // don't get interrupted by node removal.
    cardEl.replaceChildren();
    cardEl.appendChild(liveRegion.el);

    if (current.status === "closed") {
      cardEl.dataset.state = "closed";
    } else if (current.status === "scheduled") {
      cardEl.dataset.state = "scheduled";
    } else {
      cardEl.dataset.state = state.status;
    }

    const title = doc.createElement("h3");
    title.className = "polst-card__title";
    title.id = titleId;
    title.textContent = current.title;
    cardEl.appendChild(title);

    if (!options.hideBrand && current.brand) {
      const brandLine = doc.createElement("p");
      brandLine.className = "polst-card__brand";
      brandLine.textContent = `by ${current.brand.name}`;
      cardEl.appendChild(brandLine);
    }

    if (current.status === "scheduled") {
      const badge = doc.createElement("span");
      badge.className = "polst-card__status";
      badge.textContent = scheduledCopy(current, now);
      cardEl.appendChild(badge);
    } else if (current.status === "closed") {
      const badge = doc.createElement("span");
      badge.className = "polst-card__status";
      badge.textContent = "Voting closed";
      cardEl.appendChild(badge);
    }

    const optionsGrid = doc.createElement("div");
    optionsGrid.className = "polst-card__options";
    optionsGrid.setAttribute("role", "radiogroup");
    optionsGrid.setAttribute("aria-labelledby", titleId);
    optionsGrid.appendChild(renderOption("A"));
    optionsGrid.appendChild(renderOption("B"));
    cardEl.appendChild(optionsGrid);

    // POL-738 — the pre-vote DOM MUST contain no tally values (FR-008). The
    // tally subtree appears only when the viewer has cast a vote (voted or
    // optimistically pending) or when the polst is closed for voting (results
    // are public once the contest is over).
    const showTally =
      state.status === "voted" ||
      state.status === "pending" ||
      current.status === "closed";
    if (showTally) {
      cardEl.appendChild(renderTally());
    }

    if (state.status === "voted") {
      cardEl.appendChild(renderVotedBanner());
    }
    if (state.status === "error") {
      cardEl.appendChild(renderErrorBlock());
    }

    cardEl.appendChild(renderFooter());

    if (!options.hideWatermark) {
      cardEl.appendChild(renderWatermark());
    }
  }

  function renderWatermark(): HTMLElement {
    // POL-644 — Powered-by footer. Hidden when the brand's resolved
    // theme DTO has `hideWatermark: true` (gated on the
    // `embed.hideWatermark` entitlement server-side).
    const wrap = doc.createElement("div");
    wrap.className = "polst-card__watermark";
    wrap.setAttribute("data-polst-watermark", "");
    const link = doc.createElement("a");
    link.href = getAppOrigin();
    link.textContent = "Powered by Polst";
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
    wrap.appendChild(link);
    return wrap;
  }

  function renderOption(which: "A" | "B"): HTMLElement {
    const opt = which === "A" ? current.optionA : current.optionB;
    const wrap = doc.createElement("div");
    wrap.className = "polst-card__option";
    wrap.setAttribute("data-option", which);

    // <figure> wraps the image + caption so SR announces the pair as a
    // single media group. The caption IS the option label.
    const figure = doc.createElement("figure");
    figure.className = "polst-card__option-figure";

    if (opt.imageUrl) {
      const img = doc.createElement("img");
      img.className = "polst-card__option-image";
      img.src = opt.imageUrl;
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
      img.setAttribute("srcset", srcsetFor(opt.imageUrl));
      img.setAttribute("alt", opt.label ?? `Option ${which}`);
      figure.appendChild(img);
    } else {
      // Graceful fallback: big option letter so the layout doesn't
      // collapse when an author forgot an image.
      const fb = doc.createElement("div");
      fb.className = "polst-card__option-fallback";
      fb.textContent = which;
      fb.setAttribute("aria-hidden", "true");
      figure.appendChild(fb);
    }

    const caption = doc.createElement("figcaption");
    caption.className = "polst-card__option-label";
    caption.textContent = opt.label ?? `Option ${which}`;
    figure.appendChild(caption);

    wrap.appendChild(figure);

    // Only render a vote button while the polst accepts votes and the
    // user hasn't committed. Closed / scheduled polsts hide the button
    // row entirely; the voted-banner replaces the affordance post-vote.
    const votingOpen =
      current.status === "open" && (state.status === "idle" || state.status === "error");
    if (votingOpen) {
      const btn = doc.createElement("button");
      btn.type = "button";
      btn.className = "polst-card__vote";
      btn.textContent = `Vote ${which}`;
      btn.setAttribute("data-action", "vote");
      btn.setAttribute("data-option", which);
      btn.setAttribute("aria-label", `Vote for option ${opt.label ?? which}`);
      // `role="radio"` + `aria-checked` give SR users the radiogroup
      // mental model; `aria-pressed` mirrors it for UA heuristics that
      // still look at toggle buttons. `mixed` indicates the optimistic
      // pending state.
      btn.setAttribute("role", "radio");
      const isMine = state.choice === which;
      const checked =
        state.status === "pending" && isMine
          ? "mixed"
          : state.status === "voted" && isMine
          ? "true"
          : "false";
      btn.setAttribute("aria-checked", checked);
      // Note: `aria-pressed` is not a valid attribute on `role="radio"`
      // (axe-core flags it as aria-allowed-attr). The radiogroup
      // semantics are conveyed entirely through `role="radio"` +
      // `aria-checked`; toggle-button semantics would be mutually
      // exclusive.
      if (state.status === "pending") btn.setAttribute("disabled", "");
      btn.addEventListener("click", () => void handleVote(which));
      wrap.appendChild(btn);
    }
    return wrap;
  }

  function renderTally(): HTMLElement {
    const wrap = doc.createElement("div");
    wrap.className = "polst-card__tally";

    const pctA = percent(tallyA, total);
    const pctB = 100 - pctA;

    const bar = doc.createElement("div");
    bar.className = "polst-card__tally-bar";
    bar.setAttribute("role", "img");
    bar.setAttribute(
      "aria-label",
      `${tallyA} votes for A, ${tallyB} votes for B, ${total} total`,
    );
    // Decorative fill children don't need to be in the accessibility
    // tree — the aria-label on the bar itself already conveys the data.
    const fillA = doc.createElement("div");
    fillA.className = "polst-card__tally-bar-fill polst-card__tally-bar-fill--a";
    fillA.style.width = `${pctA}%`;
    const fillB = doc.createElement("div");
    fillB.className = "polst-card__tally-bar-fill polst-card__tally-bar-fill--b";
    fillB.style.width = `${pctB}%`;
    bar.appendChild(fillA);
    bar.appendChild(fillB);
    wrap.appendChild(bar);

    const labels = doc.createElement("div");
    labels.className = "polst-card__tally-labels";
    const labelA = doc.createElement("span");
    labelA.textContent = `A ${pctA}%`;
    const labelB = doc.createElement("span");
    labelB.textContent = `B ${pctB}%`;
    labels.appendChild(labelA);
    labels.appendChild(labelB);
    wrap.appendChild(labels);

    const totalLine = doc.createElement("span");
    totalLine.className = "polst-card__total";
    totalLine.textContent = `${formatNumber(total)} ${total === 1 ? "vote" : "votes"}`;
    wrap.appendChild(totalLine);

    // Decorative fill children are already covered by the bar's
    // aria-label, so hide them from AT to avoid double-reads.
    fillA.setAttribute("aria-hidden", "true");
    fillB.setAttribute("aria-hidden", "true");
    return wrap;
  }

  function renderVotedBanner(): HTMLElement {
    const banner = doc.createElement("div");
    banner.className = "polst-card__voted-banner";
    const msg = doc.createElement("span");
    const chosen = state.choice;
    const chosenLabel =
      chosen === "A" ? current.optionA.label : chosen === "B" ? current.optionB.label : null;
    msg.textContent = `You picked ${chosenLabel ?? `Option ${chosen ?? ""}`}`;
    banner.appendChild(msg);

    const revote = doc.createElement("button");
    revote.type = "button";
    revote.className = "polst-card__revote";
    revote.textContent = "Change my vote";
    revote.setAttribute("data-action", "revote");
    revote.addEventListener("click", () => handleRevote());
    banner.appendChild(revote);
    return banner;
  }

  function renderErrorBlock(): HTMLElement {
    const wrap = doc.createElement("div");
    wrap.className = "polst-card__error";
    const msg =
      lastError && typeof lastError === "object" && lastError !== null && "message" in lastError
        ? String((lastError as { message?: unknown }).message ?? "Vote failed")
        : "Vote failed";
    wrap.textContent = `Something went wrong: ${msg}`;
    return wrap;
  }

  function renderFooter(): HTMLElement {
    const footer = doc.createElement("div");
    footer.className = "polst-card__footer";

    const left = doc.createElement("div");
    if (current.brand) {
      const link = doc.createElement("a");
      link.textContent = `@${current.brand.slug}`;
      link.href = `${getAppOrigin()}/${current.brand.slug}`;
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      left.appendChild(link);
    }
    footer.appendChild(left);

    if (!options.hideShare) {
      const share = doc.createElement("button");
      share.type = "button";
      share.className = "polst-card__share";
      share.textContent = "Share";
      share.setAttribute("data-action", "share");
      share.addEventListener("click", () => handleShare());
      footer.appendChild(share);
    }
    return footer;
  }

  async function handleVote(option: "A" | "B"): Promise<void> {
    if (destroyed) return;
    if (state.status === "pending" || state.status === "voted") return;
    if (current.status !== "open") return;

    // Optimistic bump: record the pre-vote values so we can roll back
    // on failure. We update the model AND re-render so the user sees
    // the commit immediately.
    const prev = { tallyA, tallyB, total, status: state.status };
    if (option === "A") tallyA += 1;
    else tallyB += 1;
    total += 1;
    state = { status: "pending", choice: option };
    render();

    try {
      await voter(current.shortId, option);
      if (destroyed) return;
      state = { status: "voted", choice: option };
      lastError = null;
      render();
      const chosenLabel =
        option === "A" ? current.optionA.label ?? "A" : current.optionB.label ?? "B";
      const pctA = percent(tallyA, total);
      liveRegion.announce(
        `Vote recorded for ${chosenLabel}. ${pctA}% A, ${100 - pctA}% B. ${total} total ${total === 1 ? "vote" : "votes"}.`,
      );
      options.onVote?.({ polstId: current.shortId, option });
    } catch (err) {
      if (destroyed) return;
      tallyA = prev.tallyA;
      tallyB = prev.tallyB;
      total = prev.total;
      state = { status: "error", choice: null };
      lastError = err;
      render();
      liveRegion.announce("Vote failed. Please try again.");
      options.onVoteError?.(err);
    }
  }

  function handleRevote(): void {
    if (destroyed) return;
    if (state.status !== "voted") return;
    // Reset the local model to the last-known server tallies + flip
    // back to idle so the vote buttons re-appear. The server records
    // the new vote via a fresh `handleVote` call.
    // Note: we do NOT decrement the optimistic tally — the server's
    // vote record already counts toward it. When the user votes again
    // the optimistic bump lands on top and reconciles on resolve.
    state = { status: "idle", choice: null };
    render();
  }

  function handleShare(): void {
    if (destroyed) return;
    const appOrigin = getAppOrigin();
    const shareUrl = current.brand
      ? `${appOrigin}/${current.brand.slug}/${current.shortId}`
      : `${appOrigin}/${current.shortId}`;
    // Use the Web Share API when available; otherwise a no-op copy
    // fallback is a UX concern for a later ticket.
    const nav =
      (doc.defaultView as unknown as { navigator?: { share?: (d: ShareData) => Promise<void> } })
        ?.navigator;
    if (nav?.share) {
      void nav.share({ title: current.title, url: shareUrl });
    }
  }

  // First paint.
  render();

  return {
    update(next: PolstCardDTO): void {
      if (destroyed) return;
      const sameId = next.shortId === current.shortId;
      current = clone(next);
      // Reset tallies to the server-provided truth on every update —
      // the optimistic overlay is never authoritative once we have a
      // fresh DTO.
      tallyA = next.tallies.optionA;
      tallyB = next.tallies.optionB;
      total = next.tallies.total;
      if (!sameId) {
        state = resolveInitialState(next);
        lastError = null;
      } else if (next.status === "closed") {
        state = { status: "closed", choice: state.choice };
      } else if (next.status === "scheduled") {
        state = { status: "scheduled", choice: null };
      } else if (state.status === "scheduled" || state.status === "closed") {
        // Transitioning from scheduled/closed back into an open window
        // means the old dataset.state is stale — drop back to idle.
        state = { status: "idle", choice: null };
      }
      render();
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      if (cardEl.parentNode) cardEl.parentNode.removeChild(cardEl);
    },
    getState(): PolstCardState {
      // Defensive copy: consumers shouldn't be able to mutate the
      // internal state object.
      return { status: state.status, choice: state.choice };
    },
  };
}
